// @flow
import React, { Children, type Node } from 'react';
import classnames from 'classnames';

import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { IS_DISABLED, IS_ENABLED, IS_SELECTED } from 'Components/utils/product/product.utils';

export type ProductComparisonProps = {
  children?: Node,
  className?: string,
  eligibilityLabel?: string,
  isEligible?: boolean,
  label?: string,
  status?: 'isSelected' | 'isEnabled' | 'isDisabled',
  onComparisonClick?: () => mixed,
};

export function ProductComparison(props: ProductComparisonProps) {
  const {
    children,
    className,
    eligibilityLabel,
    isEligible,
    label,
    onComparisonClick,
    status = IS_ENABLED,
  } = props;

  if (!Children.count(children)) return null;

  if (!isEligible)
    return (
      <div className="relative">
        {children}
        <div className="absolute bg-overlay-white-60 flex font-bold items-center justify-center inset-0 p-5 text-base text-center z-1">
          {eligibilityLabel}
        </div>
      </div>
    );

  const isSelectable = status !== IS_DISABLED;
  const isSelected = status === IS_SELECTED;
  const buttonProps = isSelectable
    ? {
        onClick: onComparisonClick,
        type: 'button',
        title: label,
      }
    : null;

  const TagName = isSelectable ? 'button' : 'div';

  return (
    <TagName className={classnames(className, 'relative', 'reset-button')} {...buttonProps}>
      {children}
      <div
        className={classnames(
          'absolute',
          'bg-overlay-gray-darker-40',
          'flex items-center',
          'justify-center',
          'p-4',
          'inset-0',
          'text-white',
          'z-1',
          'rounded-tiny',
          {
            'cursor-pointer': isSelectable,
          },
        )}
      >
        {isSelectable && (
          <div
            className={classnames(
              'flex flex-col',
              'items-center',
              'opacity-100',
              'p-2',
              'rounded-full',
              'border-2',
              'transition-colors',
              {
                'bg-transparent border-white': !isSelected,
              },
              {
                'bg-emerald border-emerald': isSelected,
              },
            )}
          >
            <Icon name={isSelected ? ICONS.CHECK : ICONS.PLUS} width="1.25rem" />
          </div>
        )}
      </div>
    </TagName>
  );
}
